import React, { Children } from 'react';
import { LegalLayout } from '../components/LegalLayout';
import { Link } from 'react-router-dom';
export function PrivacyPage() {
  const sections = [{
    id: 'introduction',
    title: 'Introduction'
  }, {
    id: 'information-we-collect',
    title: 'Information We Collect'
  }, {
    id: 'source-citations',
    title: 'Source Citations and Evidence'
  }, {
    id: 'ai-inputs-outputs',
    title: 'AI Inputs and Outputs'
  }, {
    id: 'how-we-use',
    title: 'How We Use Information'
  }, {
    id: 'how-we-share',
    title: 'How We Share Information'
  }, {
    id: 'platform-integrations',
    title: 'Platform Integrations and OAuth'
  }, {
    id: 'cookies-analytics',
    title: 'Cookies and Analytics'
  }, {
    id: 'data-retention',
    title: 'Data Retention'
  }, {
    id: 'user-choices',
    title: 'User Choices and Privacy Rights'
  }, {
    id: 'children',
    title: 'Children and Minors'
  }, {
    id: 'security',
    title: 'Security'
  }, {
    id: 'international',
    title: 'International Users'
  }, {
    id: 'changes',
    title: 'Changes to this Policy'
  }, {
    id: 'contact',
    title: 'Contact Us'
  }];
  return <LegalLayout title="Privacy Policy" subtitle="How we collect, use, and protect your data." lastUpdated="06/01/26" sections={sections} data-id="element-210">
      <section id="introduction" className="mb-12" data-id="element-211">
        <h2 data-id="element-212">Introduction</h2>
        <p data-id="element-213">
          Pulsewave helps creators understand audience signals, discover
          business and content opportunities, and validate ideas with fans. To
          provide these services, we collect and use information from creators,
          their audiences, and connected platforms.
        </p>
        <p data-id="element-214">
          This Privacy Policy explains what information we collect, how we use
          it, and your choices. By using Pulsewave, you agree to the collection
          and use of information in accordance with this policy.
        </p>
      </section>

      <div className="w-full h-px bg-white/5 my-12" data-id="element-215" />

      <section id="information-we-collect" className="mb-12" data-id="element-216">
        <h2 data-id="element-217">Information We Collect</h2>

        <h3 data-id="element-218">Creator Account Information</h3>
        <p data-id="element-219">
          When you create an account, we collect your name, email address, login
          credentials, creator profile details, channel handles, niche, audience
          description, business goals, monetization status, constraints, ideas,
          preferred launch types, excluded launch types, and team/resource
          information.
        </p>

        <h3 data-id="element-220">Public Audience and Content Data</h3>
        <p data-id="element-221">
          We analyze publicly available information to provide our services,
          including public videos, titles, descriptions, posts, comments,
          engagement signals, recurring questions, audience language, and other
          publicly available creator and audience signals.
        </p>

        <h3 data-id="element-222">Connected Platform Data</h3>
        <p data-id="element-223">
          If you authorize connections through OAuth or platform integrations
          (such as YouTube/Google, Instagram/Meta, TikTok, and others), we
          collect data from those platforms. This may include analytics, views,
          watch time, retention, traffic sources, demographics, comments,
          captions, posts, metadata, and engagement metrics.
        </p>

        <h3 data-id="element-224">Creator-Granted Private Data</h3>
        <p data-id="element-225">
          You may choose to provide optional private materials, such as
          newsletters, Discord/community data, direct messages, store/customer
          notes, sponsor feedback, research notes, and screenshots. We only
          collect this data if you explicitly provide it.
        </p>

        <h3 data-id="element-226">Fan Participation Data</h3>
        <p data-id="element-227">
          When fans participate in Pulse Markets or micro-tests, we collect
          information they submit or generate, including usernames, profile
          information, predictions, points/reputation activity, votes, comments,
          "takes," market interactions, and usage data.
        </p>

        <h3 data-id="element-228">Usage and Device Data</h3>
        <p data-id="element-229">
          We automatically collect data about how you interact with our
          services, including IP address, browser type, device information, log
          data, pages viewed, feature usage, referral source, cookies, and
          analytics identifiers.
        </p>
      </section>

      <div className="w-full h-px bg-white/5 my-12" data-id="element-230" />

      <section id="source-citations" className="mb-12" data-id="element-231">
        <h2 data-id="element-232">Source Citations and Evidence</h2>
        <p data-id="element-233">
          To help creators verify why an AI recommendation was made, Pulsewave
          may store excerpts, snapshots, links, metadata, source type, recency,
          and context from source materials.
        </p>
      </section>

      <div className="w-full h-px bg-white/5 my-12" data-id="element-234" />

      <section id="ai-inputs-outputs" className="mb-12" data-id="element-235">
        <h2 data-id="element-236">AI Inputs and Outputs</h2>
        <p data-id="element-237">
          Pulsewave processes user content, audience signals, market results,
          and creator reactions to generate pitches, idea rankings, validation
          questions, market suggestions, briefs, summaries, launch
          recommendations, and related outputs.
        </p>
        <p data-id="element-238">
          <strong data-id="element-239">Our commitment to your data:</strong>
        </p>
        <ul data-id="element-240">
          <li data-id="element-241">
            We may use creator activity to personalize recommendations for that
            specific creator.
          </li>
          <li data-id="element-242">
            We may use aggregated or de-identified information to improve our
            product.
          </li>
          <li data-id="element-243">
            We <strong data-id="element-244">do not</strong> use private creator-granted data,
            connected account data, or non-public audience data to train
            general-purpose AI models without your explicit permission.
          </li>
        </ul>
      </section>

      <div className="w-full h-px bg-white/5 my-12" data-id="element-245" />

      <section id="how-we-use" className="mb-12" data-id="element-246">
        <h2 data-id="element-247">How We Use Information</h2>
        <p data-id="element-248">We use the information we collect to:</p>
        <ul data-id="element-249">
          <li data-id="element-250">Provide, operate, and maintain Pulsewave.</li>
          <li data-id="element-251">Generate AI-powered pitches and recommendations.</li>
          <li data-id="element-252">Build and maintain creator knowledge bases.</li>
          <li data-id="element-253">Create source citations and evidence records.</li>
          <li data-id="element-254">Operate Pulse Markets and micro-tests.</li>
          <li data-id="element-255">
            Produce weekly audience intelligence briefs and launch briefs.
          </li>
          <li data-id="element-256">Personalize and improve recommendations.</li>
          <li data-id="element-257">Analyze product usage and improve features.</li>
          <li data-id="element-258">Communicate with users and provide support.</li>
          <li data-id="element-259">Maintain safety, security, and integrity.</li>
          <li data-id="element-260">
            Enforce our <Link to="/terms" data-id="element-261">Terms of Service</Link> and legal
            rights.
          </li>
          <li data-id="element-262">Comply with applicable laws.</li>
        </ul>
      </section>

      <div className="w-full h-px bg-white/5 my-12" data-id="element-263" />

      <section id="how-we-share" className="mb-12" data-id="element-264">
        <h2 data-id="element-265">How We Share Information</h2>
        <p data-id="element-266">We may share information in the following circumstances:</p>
        <ul data-id="element-267">
          <li data-id="element-268">
            <strong data-id="element-269">With creators and team members:</strong> As part of the core
            service functionality.
          </li>
          <li data-id="element-270">
            <strong data-id="element-271">With fans/participants:</strong> Depending on specific
            market settings and visibility choices.
          </li>
          <li data-id="element-272">
            <strong data-id="element-273">With service providers:</strong> Such as hosting, analytics,
            authentication, email, AI model providers, databases, customer
            support, payments, and security tools.
          </li>
          <li data-id="element-274">
            <strong data-id="element-275">With connected platforms:</strong> As needed to provide
            requested integrations.
          </li>
          <li data-id="element-276">
            <strong data-id="element-277">For legal and safety purposes:</strong> To comply with legal
            obligations, protect rights and safety, and enforce our terms.
          </li>
          <li data-id="element-278">
            <strong data-id="element-279">During business transfers:</strong> In connection with a
            merger, acquisition, or sale of assets.
          </li>
          <li data-id="element-280">
            <strong data-id="element-281">In aggregated or de-identified form:</strong> Where the data
            cannot reasonably be used to identify you.
          </li>
        </ul>
        <p data-id="element-282">
          Pulsewave does not sell personal information in the traditional sense.
        </p>
      </section>

      <div className="w-full h-px bg-white/5 my-12" data-id="element-283" />

      <section id="platform-integrations" className="mb-12" data-id="element-284">
        <h2 data-id="element-285">Platform Integrations and OAuth</h2>
        <p data-id="element-286">
          Creators can connect third-party platforms to Pulsewave. We only
          access data you explicitly authorize. Authentication tokens are stored
          securely and separately. You can disconnect integrations at any time.
        </p>
        <p data-id="element-287">
          If disconnected, Pulsewave stops collecting new data from that
          platform, but may retain previously collected or derived data unless
          deletion is requested, subject to legal, security, or backup
          requirements.
        </p>
      </section>

      <div className="w-full h-px bg-white/5 my-12" data-id="element-288" />

      <section id="cookies-analytics" className="mb-12" data-id="element-289">
        <h2 data-id="element-290">Cookies and Analytics</h2>
        <p data-id="element-291">
          We use cookies and similar tracking technologies to track activity on
          our service, analyze product usage, and improve our offerings. You can
          instruct your browser to refuse all cookies or to indicate when a
          cookie is being sent. However, if you do not accept cookies, you may
          not be able to use some portions of our service.
        </p>
      </section>

      <div className="w-full h-px bg-white/5 my-12" data-id="element-292" />

      <section id="data-retention" className="mb-12" data-id="element-293">
        <h2 data-id="element-294">Data Retention</h2>
        <p data-id="element-295">
          Pulsewave retains data while accounts are active and as needed for
          product functionality, historical idea/market records, legal
          compliance, security, backups, dispute resolution, and legitimate
          business purposes. Requesting account deletion may result in the
          deletion or de-identification of your data, subject to legal and
          operational exceptions.
        </p>
      </section>

      <div className="w-full h-px bg-white/5 my-12" data-id="element-296" />

      <section id="user-choices" className="mb-12" data-id="element-297">
        <h2 data-id="element-298">User Choices and Privacy Rights</h2>
        <p data-id="element-299">
          You have the right to access, correct, delete, or export your personal
          information. You may also unsubscribe from promotional emails,
          disconnect third-party platforms, and submit privacy requests. Certain
          jurisdictions may provide additional rights regarding your personal
          information.
        </p>
      </section>

      <div className="w-full h-px bg-white/5 my-12" data-id="element-300" />

      <section id="children" className="mb-12" data-id="element-301">
        <h2 data-id="element-302">Children and Minors</h2>
        <p data-id="element-303">
          Pulsewave is not directed to children under 13. Users under 13 may not
          use the service. Users under 18 need parent or guardian permission to
          use Pulsewave.
        </p>
      </section>

      <div className="w-full h-px bg-white/5 my-12" data-id="element-304" />

      <section id="security" className="mb-12" data-id="element-305">
        <h2 data-id="element-306">Security</h2>
        <p data-id="element-307">
          We use reasonable administrative, technical, and physical safeguards
          to protect your personal information. However, no system or
          transmission over the internet is perfectly secure, and we cannot
          guarantee absolute security.
        </p>
      </section>

      <div className="w-full h-px bg-white/5 my-12" data-id="element-308" />

      <section id="international" className="mb-12" data-id="element-309">
        <h2 data-id="element-310">International Users</h2>
        <p data-id="element-311">
          Your information may be transferred to, and processed in, the United
          States and other countries where our service providers operate. Data
          protection laws in these countries may differ from those in your
          jurisdiction.
        </p>
      </section>

      <div className="w-full h-px bg-white/5 my-12" data-id="element-312" />

      <section id="changes" className="mb-12" data-id="element-313">
        <h2 data-id="element-314">Changes to this Policy</h2>
        <p data-id="element-315">
          We may update this Privacy Policy from time to time. We will notify
          you of any changes by posting the new Privacy Policy on this page and
          updating the "Last updated" date.
        </p>
      </section>

      <div className="w-full h-px bg-white/5 my-12" data-id="element-316" />

      <section id="contact" className="mb-12" data-id="element-317">
        <h2 data-id="element-318">Contact Us</h2>
        <p data-id="element-319">
          If you have any questions about this Privacy Policy, please contact us
          at:
        </p>
        <p data-id="element-320">
          <strong data-id="element-321">Foundation Lab, LLC</strong>
          <br data-id="element-322" />
          Email: support@pulsewaveco.com
        </p>
      </section>
    </LegalLayout>;
}